import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSelectModule } from '@angular/material/select';
import { NgxCurrencyDirective } from 'ngx-currency';

import { SelectPesquisavelComponent } from '../../../../../shared/components/select-pesquisavel/select-pesquisavel.component';
import { PercentualProgressBarComponent } from '../../../../../shared/components/percentual-progress-bar/percentual-progress-bar.component';
import { ConfirmModalComponent } from '../../../../../shared/components/confirm-modal/confirm-modal.component';
import {
  RateioConfigPickerModalComponent,
  RateioConfigPickerResultItem,
} from '../../../../../shared/components/rateio-config-picker-modal/rateio-config-picker-modal.component';
import { CentroCusto } from '../../../../../shared/services/models/financas/centro-custo.model';
import { RateioCentroCusto } from '../../../../../shared/services/models/financas/rateio-centro-custo.model';
import { minArrayLengthValidator } from '../../../../../shared/validators/min-array-length.validator';
import { UtilService } from '@shared/services/util.service';

@Component({
  selector: 'app-contas-receber-centro-custo',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatSlideToggleModule,
    MatSelectModule,
    MatDialogModule,
    NgxCurrencyDirective,
    SelectPesquisavelComponent,
    PercentualProgressBarComponent,
  ],
  templateUrl: './contas-receber-centro-custo.component.html',
  styleUrl: './contas-receber-centro-custo.component.css',
})
export class ContasReceberCentroCustoComponent implements OnInit, OnChanges {
  @Input() parentForm!: FormGroup;
  @Input() valorTotalDoc: number = 0;
  @Input() listaCentrosCusto: CentroCusto[] = [];
  @Input() listaGridConfiguracoes: RateioCentroCusto[] = [];
  @Input() currencyMask: any;
  @Input() percentageMask: any;
  @Input() isCentroCustoObrigatorio: boolean = false;

  get rateioGroup(): FormGroup {
    return this.parentForm.get('rateio') as FormGroup;
  }

  get rateioItensArray(): FormArray {
    return this.rateioGroup.get('itens') as FormArray;
  }

  get idCentroCustoControl(): any {
    return this.parentForm.get('dadosGerais.idCentroCusto');
  }
  get isRateioControl(): any {
    return this.parentForm.get('dadosGerais.isRateio');
  }
  get isRateioAtivo(): boolean {
    return this.parentForm.get('dadosGerais.isRateio')?.value === true;
  }

  get somaPercentualRateio(): number {
    return this.rateioItensArray.controls.reduce((acc, ctrl) => {
      return acc + (parseFloat(ctrl.get('percentual')?.value) || 0);
    }, 0);
  }

  getControlFromItem(index: number, controlName: string): any {
    return this.rateioItensArray.at(index).get(controlName);
  }

  constructor(private fb: FormBuilder, private dialog: MatDialog) { }

  ngOnInit(): void {
    // Listener no toggle de rateio
    this.parentForm.get('dadosGerais.isRateio')?.valueChanges.subscribe((ativo: boolean) => {
      if (ativo) {
        this.idCentroCustoControl?.disable();
        this.idCentroCustoControl?.setValue(null);
        if (this.rateioItensArray.length === 0) {
          this.adicionarRateio();
        }
      } else {
        this.idCentroCustoControl?.enable();
        this.rateioItensArray.clear();
      }
      this.reaplicarValidadorCentroCusto(ativo);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Quando o valorTotal muda, recalcular os valores das linhas de rateio com base nos percentuais
    if (changes['valorTotalDoc'] && !changes['valorTotalDoc'].firstChange) {
      this.recalcularValoresPorPercentual();
    }
    // Quando a lista de CCs chega (lookups async), re-aplicar o estado de disable do CC principal
    if (changes['listaCentrosCusto']) {
      const isRateio = this.parentForm.get('dadosGerais.isRateio')?.value;
      if (isRateio) {
        this.idCentroCustoControl?.disable();
      }
    }
    // Quando isCentroCustoObrigatorio chega (config async), reaplicar validadores
    if (changes['isCentroCustoObrigatorio']) {
      const isRateio = this.parentForm.get('dadosGerais.isRateio')?.value ?? false;
      this.reaplicarValidadorCentroCusto(isRateio);
    }
  }

  private reaplicarValidadorCentroCusto(isRateio: boolean): void {
    const ctrl = this.idCentroCustoControl;
    const itens = this.rateioItensArray;

    if (!isRateio && this.isCentroCustoObrigatorio) {
      ctrl?.setValidators(Validators.required);
    } else {
      ctrl?.clearValidators();
    }
    ctrl?.updateValueAndValidity({ emitEvent: false });

    if (isRateio && this.isCentroCustoObrigatorio) {
      itens.setValidators(minArrayLengthValidator(1));
    } else {
      itens.clearValidators();
    }
    itens.updateValueAndValidity({ emitEvent: false });
  }

  public adicionarRateio(): void {
    this.rateioItensArray.push(
      this.fb.group({
        idCentroCusto: [null, Validators.required],
        valor: [0],
        percentual: [0],
      })
    );
  }

  public removerRateio(index: number): void {
    this.rateioItensArray.removeAt(index);
  }

  public abrirSeletorRateio(): void {
    const possuiLinhasPreenchidas = this.rateioItensArray.controls.some(
      ctrl => !!ctrl.get('idCentroCusto')?.value
    );

    if (!possuiLinhasPreenchidas) {
      this.abrirModalConfiguracaoRateio();
      return;
    }

    const confirmRef = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Substituir rateio atual?',
        description:
          'Já existem linhas de rateio preenchidas. Ao selecionar uma configuração, todas as linhas atuais serão substituídas pela configuração escolhida. Deseja continuar?',
        buttonConfirm: 'Continuar',
        buttonClose: 'Cancelar',
      },
    });

    confirmRef.afterClosed().subscribe(result => {
      if (result?.confirm) {
        this.abrirModalConfiguracaoRateio();
      }
    });
  }

  private abrirModalConfiguracaoRateio(): void {
    const dialogRef = this.dialog.open(RateioConfigPickerModalComponent, {
      width: '520px',
      data: {
        configs: this.listaGridConfiguracoes,
        centrosCusto: this.listaCentrosCusto,
      },
    });

    dialogRef.afterClosed().subscribe((itens?: RateioConfigPickerResultItem[]) => {
      if (!itens?.length) return;

      this.rateioItensArray.clear();
      itens.forEach(item => {
        const val = this.valorTotalDoc > 0 ? (item.percentual / 100) * this.valorTotalDoc : 0;
        this.rateioItensArray.push(
          this.fb.group({
            idCentroCusto: [item.idCentroCusto, Validators.required],
            valor: [val],
            percentual: [item.percentual],
          })
        );
      });
    });
  }

  private recalcularValoresPorPercentual(): void {
    this.rateioItensArray.controls.forEach(ctrl => {
      const pct = UtilService.arredondarAbnt(parseFloat(ctrl.get('percentual')?.value) || 0, 2);
      if (this.valorTotalDoc > 0) {
        const val = UtilService.arredondarAbnt((pct / 100) * this.valorTotalDoc, 2);
        ctrl.get('valor')?.setValue(val, { emitEvent: false });
      }
    });
  }

  public recalculoOrigemValor(index: number): void {
    const row = this.rateioItensArray.at(index);
    const val = UtilService.arredondarAbnt(parseFloat(row.get('valor')?.value) || 0, 2);
    if (this.valorTotalDoc > 0) {
      const pct = UtilService.arredondarAbnt((val / this.valorTotalDoc) * 100, 2);
      row.patchValue({ percentual: pct }, { emitEvent: false });
    }
  }

  public recalculoOrigemPercentual(index: number): void {
    const row = this.rateioItensArray.at(index);
    const pct = UtilService.arredondarAbnt(parseFloat(row.get('percentual')?.value) || 0, 2);
    if (this.valorTotalDoc > 0) {
      const val = UtilService.arredondarAbnt((pct / 100) * this.valorTotalDoc, 2);
      row.patchValue({ valor: val }, { emitEvent: false });
    }
  }
}
